import {
    Col,
    Card,
    Row,
    Container,
    CardBody,
    CardHeader,
    Form,
    Button,
    InputGroup,
    Offcanvas,
    Spinner
} from "react-bootstrap"
import { useState, useEffect } from 'react'
import { USER_SCHEMA } from "../../constants/user"
import { getRoles } from "../../slices/userReducer"
import { useSelector, useDispatch } from "react-redux"
import _ from 'lodash'
import { ROLE_PERMISSIONS } from "../../constants/user"
import * as Yup from 'yup';
export default function UserForm(props) {
    const { submitFunc, isUpdate = false, user, show, onHide } = props
    const [userData, setUserData] = useState({ name: null, email: null, phone: null, role: 0 })

    const dispatch = useDispatch()
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    const [validated, setValidated] = useState(false)
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)
    const roles = useSelector(state => state.user.roles)

    useEffect(() => {
        if (show) {
            setErrors({}) // Clear errors when modal opens
            setValidated(false) // Also reset validation state
        }
    }, [show])

    useEffect(() => {
        dispatch(getRoles())
    }, [])

    useEffect(() => {
        if (isUpdate && !!user.name) {
            const { name, email, phone, role } = user
            setUserData({ name, email, phone, role: role.id })
            setSelectedPermissions(_.map(role.permissions, 'codename'))
        }
    }, [isUpdate, user])

    useEffect(() => {
        if (Array.isArray(roles) && roles.length) {
            setSelectedPermissions(_.map(roles[0].permissions, 'codename'))
        }
    }, [roles])

    const handleFieldValidation = async (e) => {
        const { name, value } = e.target;

        try {
            await Yup.reach(USER_SCHEMA(isUpdate), name).validate(value);
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        } catch (validationError) {
            setErrors((prev) => ({
                ...prev,
                [name]: validationError.message,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (typeof submitFunc === 'function') {
            let values = {
                name: null,
                email: null,
                phone: '',
                user_role: null
            }

            if (!isUpdate) {
                values['password'] = null
                values['r_password'] = null
            }

            if (e.target) {
                values = {
                    name: e.target['name'].value,
                    email: e.target['email'].value,
                    phone: e.target['phone'].value,
                    user_role: e.target['user_role'].value
                }
            }
            try {
                const data = await USER_SCHEMA(isUpdate).validate(values, { abortEarly: false });
                setValidated(true)
                setErrors({});

                // Set loading to true before calling submitFunc
                setLoading(true)

                // Call the callback function and wait for it to complete
                await new Promise((resolve, reject) => {
                    try {
                        const result = submitFunc(data);

                        // If submitFunc returns a promise, handle it
                        if (result && typeof result.then === 'function') {
                            result.then(resolve).catch(reject);
                        } else {
                            // If it doesn't return a promise, add a delay to show loading state
                            setTimeout(() => resolve(result), 100);
                        }
                    } catch (error) {
                        reject(error);
                    }
                })

                // If successful, you might want to close the modal or reset form
                // onHide() // Uncomment if you want to close the modal after successful submission

            } catch (err) {
                // Handle validation errors (from Yup schema)
                if (err.inner && Array.isArray(err.inner)) {
                    const errData = {}
                    err.inner.forEach(e => {
                        errData[e.path] = e.message
                    });
                    setValidated(false)
                    setErrors(errData);
                } else {
                    // Handle submitFunc errors (API errors, etc.)
                    console.error('Submit function error:', err)
                    // You might want to show a toast or error message here
                    // The error is already handled in the parent component
                }
            } finally {
                // Always set loading to false when done
                setLoading(false)
            }
        }
    }

    const handleChange = (e) => {
        const role_id = e.target.value;
        const user_role = roles.filter(role => role.id == role_id)
        if (Array.isArray(user_role) && user_role.length) {
            setSelectedPermissions(_.map(user_role[0].permissions, 'codename'))
        }
        setUserData({ ...userData, role: role_id })
    }

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, '');
        e.target.value = numericValue;

        // Clear phone error if user reaches 10 digits
        if (numericValue.length === 10 && errors.phone) {
            setErrors(prev => ({ ...prev, phone: null }));
        }
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <h4 className="!text-2xl mb-0">{isUpdate ? 'Edit' : 'New'} User Information</h4>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Container fluid className="p-0">
                    <Card className="border-0 shadow-none">
                        <CardBody className="p-0">
                            <Form noValidate onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="name">Name:</Form.Label><span className="text-danger">*</span>
                                            <InputGroup hasValidation>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Name"
                                                    id="validationCustomName"
                                                    name="name"
                                                    isInvalid={!!errors.name}
                                                    defaultValue={isUpdate ? userData.name : ''}
                                                    onBlur={handleFieldValidation}
                                                    onChange={handleFieldValidation}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="phone">Mobile Number:</Form.Label>
                                            <InputGroup hasValidation>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Phone Number"
                                                    id="phone"
                                                    maxLength={10}
                                                    name="phone"
                                                    isInvalid={!!errors.phone}
                                                    defaultValue={isUpdate ? userData.phone : ''}
                                                    onBlur={handleFieldValidation}
                                                    onChange={(e) => {
                                                        handlePhoneChange(e);
                                                        handleFieldValidation(e);
                                                    }}
                                                    onKeyPress={(e) => {
                                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="email">Email:</Form.Label><span className="text-danger">*</span>
                                            <InputGroup hasValidation>
                                                <Form.Control
                                                    type="text"
                                                    id="email"
                                                    placeholder="Enter Email"
                                                    name="email"
                                                    isInvalid={!!errors.email}
                                                    defaultValue={isUpdate ? userData.email : ''}
                                                    onBlur={handleFieldValidation}
                                                    onChange={handleFieldValidation}
                                                />

                                                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                </Row>

                                <hr className="my-4" />

                                <Row>
                                    <Col xs={12}>
                                        <div className="header-title mb-3">
                                            <Card.Title as="h5">Roles & Permissions</Card.Title>
                                        </div>
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="user_role">User Role:</Form.Label>
                                            <Form.Select
                                                name="user_role"
                                                onChange={handleChange}
                                                value={userData.role}
                                            >
                                                {roles.map(role => {
                                                    return <option key={role.id} value={role.id}>{role.name}</option>
                                                })}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="permissions-grid">
                                            <Row>
                                                {Object.keys(ROLE_PERMISSIONS).map(role_key => {
                                                    return (
                                                        <Col xs={12} sm={6} className="mb-2" key={role_key}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                label={ROLE_PERMISSIONS[role_key]}
                                                                disabled
                                                                checked={selectedPermissions.includes(role_key)}
                                                            />
                                                        </Col>
                                                    )
                                                })}
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                    <Button type="button" variant="secondary" onClick={onHide} className="fw-bold">
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="fw-bold"
                                        disabled={loading} // Disable submit button during loading
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                {isUpdate ? 'Updating...' : 'Adding...'}
                                            </>
                                        ) : (
                                            `${isUpdate ? 'Update' : 'Add'} User`
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            </Offcanvas.Body>
        </Offcanvas>
    )
}